import { useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { t } from "i18next";
import MySocialIcon from "components/shared/MySocialIcon";
import capitalize from "components/ui/utils/capitalize";

const DEFAULT_ZOOM = 0.9;

/**
 * Right pane of the post composer: the network preview rendered read-only.
 * It re-renders live as the form on the left mutates the shared post group
 * state. Tall (TikTok 9:16) and wide (Medium 750px) previews are scaled to
 * fit the pane with the same CSS `zoom` approach the legacy editor uses.
 */
const ComposerPreviewPane = ({
    PreviewComponent,
    posts,
    activeSocialNetwork,
    postIndex,
    postGroup,
    className = "",
    onAssetDelete,
    onAssetEdit,
    onAssetNew,
    onTextEdit,
    onTitleEdit,
    onLinkEdit,
    onCreateNetworkPost,
    onDeleteNetworkPost,
    onDeleteIndexPost,
    onPreferencesEdit,
    onCoverEdit,
    onCommentsToCreateEdit
}) => {

    const paneRef = useRef(null);
    const cardRef = useRef(null);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);

    const networkPosts = posts?.filter(e => e?.network == activeSocialNetwork);
    const activeMediaProductType = networkPosts?.[0]?.preferences?.media_product_type;

    useLayoutEffect(() => {
        const pane = paneRef.current;
        const card = cardRef.current;
        if (!pane || !card) return;

        const fit = () => {
            if (activeSocialNetwork === "medium") {
                // Medium articles are designed at 750px wide and read like a
                // blog — scale to pane width and let them scroll vertically.
                const next = Math.max(0.55, Math.min(1, (pane.clientWidth - 48) / 750));
                setZoom(prev => Math.abs(prev - next) > 0.02 ? next : prev);
                return;
            }
            // Every other network: scale the card down so the post fits the
            // pane — but never below a readability floor, or captions become
            // illegible. Longer posts scroll within the pane instead. TikTok
            // is a single 9:16 frame with no long text, so it may shrink
            // further to always fit fully.
            const minZoom = activeSocialNetwork === "tiktok" ? 0.55 : 0.78;
            const currentZoom = parseFloat(card.style.zoom) || 1;
            const naturalHeight = card.scrollHeight / currentZoom;
            if (!naturalHeight) return;
            const next = Math.max(minZoom, Math.min(DEFAULT_ZOOM, (pane.clientHeight - 40) / naturalHeight));
            setZoom(prev => Math.abs(prev - next) > 0.02 ? next : prev);
        };

        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(pane);
        observer.observe(card);
        return () => observer.disconnect();
    }, [activeSocialNetwork, networkPosts?.length, activeMediaProductType]);

    if (!PreviewComponent || !networkPosts?.length) return <></>;

    return (
        <div className={`flex-col min-h-0 border-l border-gray-200 dark:border-gray-700 bg-tertiary-50 dark:bg-gray-900 bg-[radial-gradient(circle,rgba(148,163,184,0.25)_1px,transparent_1px)] dark:bg-[radial-gradient(circle,rgba(55,65,81,0.5)_1px,transparent_1px)] bg-[length:22px_22px] ${className}`}>
            <div className="shrink-0 h-11 px-5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-tertiary-400 dark:text-gray-500">
                    {t('composer.previewLabel', 'Preview')}
                    <span>·</span>
                    <MySocialIcon network={activeSocialNetwork} className="!w-4 !h-4" wrapperClassName="!w-4 !h-4" />
                    <span className="normal-case tracking-normal text-sm font-semibold text-tertiary-600 dark:text-gray-300">
                        {capitalize(activeSocialNetwork)}
                    </span>
                </div>
            </div>
            <div ref={paneRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex justify-center items-start px-4 py-4 lg:px-6 lg:pb-6">
                <AnimatePresence exitBeforeEnter>
                    <motion.div
                        key={activeSocialNetwork}
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        transition={{ duration: 0.15 }}
                        className={`w-full h-fit my-auto ${activeSocialNetwork == "medium" ? "max-w-[750px]" : "max-w-[460px]"}`}
                    >
                        <div
                            ref={cardRef}
                            style={{ zoom }}
                            className="w-full rounded-2xl overflow-hidden bg-secondary-500 dark:bg-gray-800 shadow-evenly-lg ring-1 ring-black/5 dark:ring-white/10"
                        >
                            <PreviewComponent
                                className="w-full !max-w-none"
                                imgClassName="!aspect-square"
                                muted={true}
                                isEditable={false}
                                isSimpleView={true}
                                postIndex={postIndex}
                                onAssetDelete={onAssetDelete}
                                onAssetEdit={onAssetEdit}
                                onAssetNew={onAssetNew}
                                onTextEdit={onTextEdit}
                                onTitleEdit={onTitleEdit}
                                onLinkEdit={onLinkEdit}
                                onCreateNetworkPost={onCreateNetworkPost}
                                onDeleteNetworkPost={onDeleteNetworkPost}
                                onDeleteIndexPost={onDeleteIndexPost}
                                onPreferencesEdit={onPreferencesEdit}
                                onCoverEdit={onCoverEdit}
                                onCommentsToCreateEdit={onCommentsToCreateEdit}
                                allPosts={posts}
                                postGroup={postGroup}
                                {...networkPosts?.length > 1 ? networkPosts : networkPosts?.[0]}
                            />
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ComposerPreviewPane;
